import type { Dispatch } from 'redux';
import { setIn } from 'timm';
import { call } from 'typed-redux-saga';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import { createLogger } from '@owl-lib/logger';
import type {
  AGENTIC_VERBS,
  Message,
  Summary,
  Thread,
} from '../../api/claims/interface';
import searchApiClient, { TIMEOUT_MS } from '../../api/search/client';
import { SearchType } from '../../api/search/interface';
import { globalBus } from '../../context/event-bus';
const logger = createLogger(__filename);

export enum SEARCH_EMIT_EVENTS {
  SearchAnswerAskOwl = 'search_answer_ask_owl',
  SearchStatusAskOwl = 'search_status_ask_owl',
  NewSearchAskOwl = 'new_search_ask_owl',
  CancelSearchAskOwl = 'cancel_search_ask_owl',
  SetThreadId = 'set_thread_id',
}

export interface SearchState {
  answer: {
    [dossier_id: string]: {
      prompt: string;
      message?: string;
      status?: 'in_progress' | 'done' | 'error';
      searchType?: SearchType;
    };
  };
  conversationalAnswer: {
    [dossier_id: string]: {
      prompt: string;
      message?: string;
      status?: 'in_progress' | 'done' | 'error';
    };
  };
  threads: {
    [dossier_id: string]: {
      [thread_id: string]: Thread;
    };
  };
  thread_messages: {
    [dossier_id: string]: {
      [thread_id: string]: Message[];
    };
  };
  actions: {
    search: AsyncActionState;
    agenticSearch: AsyncActionState;
    conversationalSearch: AsyncActionState;
    getThreads: AsyncActionState;
    getMessages: AsyncActionState;
    deleteThread: AsyncActionState;
  };
}

const initialState: SearchState = {
  answer: {},
  conversationalAnswer: {},
  threads: {},
  thread_messages: {},
  actions: {
    search: { status: 'uninitialized' },
    agenticSearch: { status: 'uninitialized' },
    conversationalSearch: { status: 'uninitialized' },
    getThreads: { status: 'uninitialized' },
    getMessages: { status: 'uninitialized' },
    deleteThread: { status: 'uninitialized' },
  },
};

function* search(action: {
  payload: {
    dispatch: Dispatch<any>;
    dossierId: string;
    query: string;
    dataVersion?: string;
    searchType?: SearchType;
    threadId: string | null;
    conversational?: boolean;
  };
}) {
  const { dossierId, dispatch } = action.payload;
  const socket = yield* call(searchApiClient.search, action.payload);

  const handleCancelSearch = () => {
    socket.close();
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'cancelled');
  };

  socket.onopen = () => {
    logger.info('WebSocket connection opened for search!');
    const payload = {
      query: action.payload.query,
      dataVersion: action.payload.dataVersion,
      searchType: action.payload.searchType,
      thread_id: action.payload.threadId,
      conversational: action.payload.conversational,
    };
    socket.send(JSON.stringify(payload));
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'in_progress');
    globalBus.emit(
      SEARCH_EMIT_EVENTS.NewSearchAskOwl,
      action.payload.searchType || SearchType.Normal
    );
  };

  socket.onmessage = (event: MessageEvent<any>) => {
    const data = JSON.parse(event.data);
    if (data.type === 'thread_created') {
      globalBus.emit(SEARCH_EMIT_EVENTS.SetThreadId, data.thread_id);
    }
    dispatch({
      type: 'search/onSearchMessage',
      payload: {
        ...data,
        dossierId,
      },
    });
  };

  socket.onclose = () => {
    logger.info('WebSocket closed from search.');
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
  };

  socket.onerror = (error) => {
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'error');
    logger.error('WebSocket Error:', error);
  };

  globalBus.on(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
}

function* conversationalSearch(action: {
  payload: {
    dispatch: Dispatch<any>;
    dossierId: string;
    query: string;
    dataVersion?: string;
    threadId: string | null;
  };
}) {
  const { dossierId, dispatch, query, dataVersion, threadId } = action.payload;
  const socket = yield* call(searchApiClient.search, {
    dossierId,
    query,
    dataVersion,
    threadId,
  });

  const handleCancelSearch = () => {
    socket.close();
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'cancelled');
  };

  socket.onopen = () => {
    logger.info('WebSocket connection opened for conversational search!');
    const payload = {
      query: action.payload.query,
      dataVersion: action.payload.dataVersion,
      thread_id: action.payload.threadId,
      conversational: true,
    };
    socket.send(JSON.stringify(payload));
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'in_progress');
    globalBus.emit(SEARCH_EMIT_EVENTS.NewSearchAskOwl, SearchType.Normal);
  };

  socket.onmessage = (event: MessageEvent<any>) => {
    const data = JSON.parse(event.data);
    if (data.type === 'thread_created') {
      globalBus.emit(SEARCH_EMIT_EVENTS.SetThreadId, data.thread_id);
    }
    dispatch({
      type: 'search/onConversationalSearchMessage',
      payload: {
        ...data,
        dossierId,
      },
    });
  };

  socket.onclose = () => {
    logger.info('WebSocket closed from conversational search.');
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
  };

  socket.onerror = (error) => {
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'error');
    logger.error('WebSocket Error:', error);
  };

  globalBus.on(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
}

function* agenticSearch(action: {
  payload: {
    dispatch: Dispatch<any>;
    dossierId: string;
    selected_text: string;
    summary: Summary;
    data_version?: string;
    verbs: AGENTIC_VERBS[];
  };
}) {
  const { dispatch, dossierId } = action.payload;
  const socket = yield* call(searchApiClient.agenticSearch, action.payload);

  const handleCancelSearch = () => {
    socket.close();
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'cancelled');
  };

  socket.onopen = () => {
    logger.info('WebSocket connection opened for agentic search');
    const payload = {
      selected_text: action.payload.selected_text,
      summary: action.payload.summary,
      data_version: action.payload.data_version,
      verbs: action.payload.verbs,
    };
    socket.send(JSON.stringify(payload));
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'in_progress');
    globalBus.emit(SEARCH_EMIT_EVENTS.NewSearchAskOwl);
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    dispatch({
      type: 'search/onSearchMessage',
      payload: {
        ...data,
        dossierId,
      },
    });
  };

  socket.onclose = () => {
    logger.info('WebSocket closed from agentic search');
    globalBus.off(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
  };

  socket.onerror = (error) => {
    globalBus.emit(SEARCH_EMIT_EVENTS.SearchStatusAskOwl, 'error');
    logger.error('WebSocket Error in agentic search:', error);
  };

  globalBus.on(SEARCH_EMIT_EVENTS.CancelSearchAskOwl, handleCancelSearch);
}

const slice = setupSlice('search', initialState)
  .addAsyncSagas(
    {
      search,
      conversationalSearch,
      agenticSearch,
      getThreads: invoke(searchApiClient.getThreads),
      getMessages: invoke(searchApiClient.getMessages),
      deleteThread: invoke(searchApiClient.deleteThread),
    },
    {
      timeoutLimit: TIMEOUT_MS,
    }
  )
  .addReducers({
    'search/pending': (state, action) => {
      state.answer = setIn(state.answer, [action.meta.arg.dossierId], {
        prompt: action.meta.arg.query,
        searchType: action.meta.arg.searchType,
        message: undefined,
        status: 'in_progress',
      }) as typeof state.answer;
    },
    'conversationalSearch/pending': (state, action) => {
      state.conversationalAnswer = setIn(
        state.conversationalAnswer,
        [action.meta.arg.dossierId],
        {
          prompt: action.meta.arg.query,
          message: undefined,
          status: 'in_progress',
        }
      ) as typeof state.conversationalAnswer;
    },
    'agenticSearch/pending': (state, action) => {
      state.answer = setIn(state.answer, [action.meta.arg.dossierId], {
        prompt: `Expanding on "${action.meta.arg.selected_text}"`,
        message: undefined,
        status: 'in_progress',
      }) as typeof state.answer;
    },
    'getThreads/fulfilled': (state, action) => {
      const threads = action.payload.threads;
      state.threads = setIn(
        state.threads,
        [action.meta.arg],
        Object.fromEntries(
          threads.map((thread: Thread) => [thread.thread_id, thread])
        )
      ) as typeof state.threads;
    },
    'getMessages/fulfilled': (state, action) => {
      const messages = action.payload.messages;
      state.thread_messages = setIn(
        state.thread_messages,
        [action.meta.arg.dossierId, action.meta.arg.threadId],
        messages
      ) as typeof state.thread_messages;
    },
    'deleteThread/fulfilled': (state, action) => {
      const { dossierId, threadId } = action.meta.arg;
      if (state.threads[dossierId] && state.threads[dossierId][threadId]) {
        const updatedThreads = { ...state.threads[dossierId] };
        delete updatedThreads[threadId];
        state.threads = setIn(
          state.threads,
          [dossierId],
          updatedThreads
        ) as typeof state.threads;
      }
      if (
        state.thread_messages[dossierId] &&
        state.thread_messages[dossierId][threadId]
      ) {
        const updatedThreadMessages = { ...state.thread_messages[dossierId] };
        delete updatedThreadMessages[threadId];
        state.thread_messages = setIn(
          state.thread_messages,
          [dossierId],
          updatedThreadMessages
        ) as typeof state.thread_messages;
      }
    },
  })
  .addReducers({
    onSearchMessage: (
      state,
      action: {
        payload: {
          dossierId: string;
          message: string;
          status: 'in_progress' | 'done' | 'error';
        };
      }
    ) => {
      const { dossierId, message, status } = action.payload;

      if (status === 'in_progress' && message !== '<<HEART_BEAT>>') {
        state.answer = setIn(
          state.answer,
          [dossierId, 'message'],
          `${state.answer[dossierId]?.message ?? ''}${message}`
        ) as typeof state.answer;
      }

      state.answer = setIn(
        state.answer,
        [dossierId, 'status'],
        status
      ) as typeof state.answer;
    },
    onConversationalSearchMessage: (
      state,
      action: {
        payload: {
          dossierId: string;
          message: string;
          status: 'in_progress' | 'done' | 'error';
        };
      }
    ) => {
      const { dossierId, message, status } = action.payload;

      if (status === 'in_progress' && message !== '<<HEART_BEAT>>') {
        state.conversationalAnswer = setIn(
          state.conversationalAnswer,
          [dossierId, 'message'],
          `${state.conversationalAnswer[dossierId]?.message ?? ''}${message}`
        ) as typeof state.conversationalAnswer;
      }

      state.conversationalAnswer = setIn(
        state.conversationalAnswer,
        [dossierId, 'status'],
        status
      ) as typeof state.conversationalAnswer;
    },
    clearConversationalAnswer: (
      state,
      action: { payload: { dossierId: string } }
    ) => {
      state.conversationalAnswer = setIn(
        state.conversationalAnswer,
        [action.payload.dossierId],
        undefined
      ) as typeof state.conversationalAnswer;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
